/**
 * @fileOverview The demo pointer: bringing it on stage, flying it, taking it away. The motion itself —
 * the bow, the easing, the overshoot — is `services/viewHelp/pointer.ts`.
 */

import { FRAME_HEIGHT, FRAME_WIDTH } from "@/services/viewHelp/camera"
import { flightTo, type Point } from "@/services/viewHelp/pointer"
import { ACTION_CAMERA_MS, prefersReducedMotion } from "./timing"
import { clearHover } from "@/services/viewHelp/hover"
import { Move, frameWindow, pointer, pointerKey, pointerVisible, scaled } from "./state"

/**
 * Where a pointer that was away reappears, relative to its target: a short approach, from whichever
 * side has room, so it arrives rather than pops up.
 */
const APPROACH: Point = { x: 110, y: 90 }

/**
 * Flies the pointer to a point in the frame, and resolves when it lands.
 *
 * Under reduced motion the pointer simply appears at the destination: the position still carries
 * the meaning ("this thing here"), it is only the travel that is decoration.
 *
 * `onMove` is called for every position the pointer takes, including the last. A drag needs it: the
 * page has to be told what the pointer passes over on its way, and the events have to carry the
 * coordinates the pointer is actually at — the gantt's selection fills the range between where the
 * button went down and whatever the pointer was last over.
 */
export async function flyPointerTo(target: Point, move: Move, onMove?: (_at: Point) => void): Promise<void> {
  if (prefersReducedMotion()) {
    pointer.value = target
    onMove?.(target)
    return
  }

  const flight = flightTo(pointer.value, target)
  if (!flight) {
    onMove?.(target)
    return
  }

  const frameMs = scaled(flight.frameMs)
  for (const point of flight.points) {
    if (!move.current) return
    pointer.value = point
    onMove?.(point)
    if (frameMs) await move.pause(frameMs)
  }
}

/**
 * Puts the pointer on stage near `target`, if it is not there already, so its flight to the target is
 * a short approach. A pointer already on stage — the second action of a step, the walk after a
 * press — simply carries on from where it is.
 */
export function bringPointerNear(target: Point) {
  if (pointerVisible.value && pointer.value) return
  const win = frameWindow.value
  const width = win?.innerWidth ?? FRAME_WIDTH
  const height = win?.innerHeight ?? FRAME_HEIGHT
  // From below and right of the target, unless that is off the frame; then from the other side.
  const dx = target.x + APPROACH.x < width - 10 ? APPROACH.x : -APPROACH.x
  const dy = target.y + APPROACH.y < height - 10 ? APPROACH.y : -APPROACH.y
  pointer.value = { x: target.x + dx, y: target.y + dy }
  pointerVisible.value = true
}

/**
 * The approach every press and every field starts with: on stage nearby, then over to `at` while the
 * camera turns towards it. True if the move is still the current one when it arrives.
 */
export async function approach(at: Point, move: Move): Promise<boolean> {
  bringPointerNear(at)
  await Promise.all([flyPointerTo(at, move), move.pause(scaled(ACTION_CAMERA_MS))])
  return move.current
}

/**
 * What a `hover` action left the pointer resting on, so that sending the pointer away also tells the
 * page it has gone — a `mouseover` the page heard needs its `mouseout`, or the column heading it lit
 * and the tooltip it opened stay behind under the next step.
 */
let restingOn: Element | null = null

export function restPointerOn(el: Element | null) {
  restingOn = el
}

/** Whether the pointer is resting on something a `hover` action put it on. */
export const isResting = () => restingOn !== null

/** Takes the pointer off stage. It fades where it is; the next action brings it back nearby. */
export function sendPointerAway() {
  pointerVisible.value = false
  pointerKey.value = null
  if (restingOn) {
    const el = restingOn
    restingOn = null
    try {
      const view = el.ownerDocument.defaultView
      if (view) {
        el.dispatchEvent(new view.MouseEvent("mouseout", { bubbles: true, cancelable: true, view }))
        // `mouseleave` does not bubble, so one dispatched on the element alone never reaches a page
        // that listens further up — and the matrix does: its tooltip and its lit column heading hang
        // off the scroller, and both stayed up under every later step. A pointer that leaves the
        // document leaves each of its ancestors as well, so each of them hears it, the way a browser
        // tells them when a real mouse leaves the window.
        for (let at: Element | null = el; at; at = at.parentElement) {
          at.dispatchEvent(new view.MouseEvent("mouseleave", { bubbles: false, cancelable: true, view }))
        }
      }
    } catch {
      // A frame between two documents — nothing left to leave.
    }
  }
  // A pointer that is gone hovers nothing: the button it last pressed would stay lit under the text.
  try {
    clearHover(frameWindow.value?.document)
  } catch {
    // A frame between two documents — nothing left to clear.
  }
}
